import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axiosInstance from "../axiosInstance";
import { toast } from "react-toastify";
import { Box, CircularProgress, Typography } from "@mui/material";
import LOGO_BASE64 from "../assets/data/logoBase64";
import dayjs from "dayjs";
import { getTrays } from "../actions";
import { calculateTotals } from "../utils/calculateTotals";

const TransactionViewer = () => {
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const initialized = useRef(false);

    const customerId = searchParams.get("customerId");
    const startDate = searchParams.get("startDate");
    const endDate = searchParams.get("endDate");

    useEffect(() => {
        // Prevent double execution in Strict Mode
        if (!customerId || initialized.current) return;
        
        initialized.current = true;
        fetchAndPrint();
    }, [customerId]);

    const fetchAndPrint = async () => {
        try {
            setLoading(true);

            const payload = { customerId };
            if (startDate && endDate) {
                payload.startDate = dayjs(startDate).format("YYYY-MM-DD");
                payload.endDate = dayjs(endDate).format("YYYY-MM-DD");
            }

            const [custRes, txRes, traysList] = await Promise.all([
                axiosInstance.post("/customers/details", { customerId }),
                axiosInstance.post("/transactions/list", payload),
                getTrays(),
            ]);

            const customerDetails = custRes?.data?.result || {};
            const transactionList = txRes?.data?.result || [];
            const trayTypes = Array.isArray(traysList) ? traysList : [];

            if (transactionList.length === 0) {
                toast.info("No transactions found for the selected period.");
                setLoading(false);
                return;
            }

            const totals = calculateTotals(transactionList);
            const { 
              grandTotalTransactionAmount,
              discountAmount,
              labourCharges,
              grandTotalPaidAmount,
              damageRefund,
              grandTrayPaymentAmount,
              totalPaid,
              totalDueAmount
            } = totals;

            generatePrint(
                transactionList,
                customerDetails,
                trayTypes,
                {
                    grandTotalTransactionAmount,
                    discountAmount,
                    labourCharges,
                    grandTotalPaidAmount,
                    grandTrayPaymentAmount,
                    damageRefund,
                    totalPaid,
                    totalDueAmount
                },
                startDate,
                endDate
            );

        } catch (err) {
            console.error(err);
            toast.error("Failed to load transaction data");
        } finally {
            setLoading(false);
        }
    };

    const generatePrint = (list, customer, trayTypes, totals, sDate, eDate) => {
        const val = (v) => v ?? "-";

        // Create the print document
        window.document.write(`
      <html>
      <head>
        <title>Transactions - ${customer?.name}</title>
        <style>
          body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 30px; color: #333; }
          .header { display: flex; justify-content: space-between; border-bottom: 3px solid #1976d2; padding-bottom: 10px; margin-bottom: 20px; }
          .company-name { font-size: 24px; font-weight: bold; color: #1976d2; }
          .summary-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 10px; margin-bottom: 20px; background: #f5f5f5; padding: 15px; border-radius: 8px; }
          .summary-item { text-align: center; }
          .summary-label { font-size: 10px; color: #666; text-transform: uppercase; }
          .summary-value { font-size: 14px; font-weight: bold; }
          
          table { width: 100%; border-collapse: collapse; font-size: 11px; }
          th { background: #f0f0f0; font-weight: bold; border: 1px solid #ccc; padding: 6px; }
          td { border: 1px solid #ccc; padding: 6px; text-align: center; }
          
          .tray-column { background-color: #e4ffe4 !important; -webkit-print-color-adjust: exact; }
          .damage-text { background-color: #ffebee; color: #c62828; font-weight: bold; }
          .labour-text { color: #1976d2; font-style: italic; }
          .proprietor-meta { font-size: 13px; color: #555; margin-top: 4px; }
        </style>
      </head>
      <body>
        <div class="header">
            <div style="display: flex; align-items: center; gap: 12px;">
                <img src="${LOGO_BASE64}" style="width: 50px; height: 50px; border-radius: 4px;" />
                <div>
                    <div style="font-size: 20px; font-weight: bold; color: #1976d2;">${customer?.employerDetails?.employerName || "-"}</div>
                    <div style="color: #666; font-size: 16px;">${customer?.branchName || "-"}</div>
                    <div class="proprietor-meta">
                        Proprietor: <strong>Maddireddy Ramesh</strong> &bull; Contact: <strong>+91 76666 77779</strong>
                    </div>
                </div>
            </div>
          <div style="text-align: right;">
            <div style="font-size: 18px; font-weight: bold;">${customer?.name || "-"}</div>
            <div>${customer?.mobile || "-"}</div>
          </div>
        </div>

        <div class="summary-grid">
          <div class="summary-item"><div class="summary-label">Total Transaction</div><div class="summary-value">₹${totals.grandTotalTransactionAmount.toLocaleString("en-IN")}</div></div>
          <div class="summary-item"><div class="summary-label">Total Discount</div><div class="summary-value">₹${totals.discountAmount.toLocaleString("en-IN")}</div></div>
          <div class="summary-item"><div class="summary-label">Total Damage Refund</div><div class="summary-value" style="color:red">₹${totals.damageRefund.toLocaleString("en-IN")}</div></div>
          <div class="summary-item"><div class="summary-label">Total Amount Paid</div><div class="summary-value" style="color:green">₹${(totals.grandTotalPaidAmount + totals.grandTrayPaymentAmount).toLocaleString("en-IN")}</div></div>
          <div class="summary-item">
            <div class="summary-label">Total Amount Due</div>
            <div class="summary-value" style="color:#1976d2">
                ₹${totals.totalDueAmount.toLocaleString("en-IN")}
            </div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th rowspan="2">Date</th>
              <th colspan="5">Sales</th>
              <th colspan="3" class="tray-column">Trays Returned</th>
              <th colspan="1" rowspan="2">Discount</th>
              <th colspan="3">Payment</th>
            </tr>
            <tr>
              <th>Item</th><th>Qty</th><th>Price</th><th>Total</th><th>Loading</th>
              <th class="tray-column">Qty</th><th class="tray-column">Price</th><th class="tray-column">Total</th>
              <th>Cash</th><th>Tray</th><th>Total</th>
            </tr>
          </thead>
          <tbody>
            ${list.map(tx => {
                let salesRows = tx.sales ? [...tx.sales] : [];

                if (tx?.damageRefunds?.length > 0) {
                    const tempDamage = tx.damageRefunds.map(d => ({ 
                        ...d, 
                        isDamage: true,
                        itemName: `${d.itemName || 'Item'} (Refund)`
                    }));
                    salesRows = [...salesRows, ...tempDamage];
                }

                const trays = tx.trays || [];
                const rowCount = Math.max(salesRows.length, trays.length, 1);
                const pAmt = Number(tx.paymentAmount || 0);
                const tTotal = trays.reduce((s, t) => s + (Number(t.numberOfTrays || 0) * Number(t.costPerTray || 0)), 0);

                return Array.from({ length: rowCount }).map((_, i) => {
                    const sale = salesRows[i];
                    const tray = trays[i];
                    
                    return `
                        <tr>
                            <!-- Column 1: Date -->
                            ${i === 0 ? `<td rowspan="${rowCount}">${dayjs(tx.dateOfTransaction).format("DD-MMM-YYYY")}</td>` : ""}
                            
                            <!-- Columns 2-5: Sales Details -->
                            <td class="${sale?.isDamage ? 'damage-text' : ''} ${sale?.isLabour ? 'labour-text' : ''}">
                                ${val(sale?.itemName)}
                            </td>
                            <td class="${sale?.isDamage ? 'damage-text' : ''}">${val(sale?.numberOfItems)}</td>
                            <td class="${sale?.isDamage ? 'damage-text' : ''}">${val(sale?.costPerItem)}</td>
                            <td class="${sale?.isDamage ? 'damage-text' : ''}">${val(sale?.total)}</td>
                            
                            <!-- Column 6: Loading Charges -->
                            ${i === 0 ? `<td rowspan="${rowCount}">${val(tx?.labourCharges?.total)}</td>` : ""}

                            <!-- Columns 7-9: Trays Details -->
                            <td class="tray-column">${val(tray?.numberOfTrays)}</td>
                            <td class="tray-column">${val(tray?.costPerTray)}</td>
                            <td class="tray-column">${tray ? (Number(tray.numberOfTrays || 0) * Number(tray.costPerTray || 0)) : "-"}</td>

                            <!-- Column 10: Discount Charges -->
                            ${i === 0 ? `<td rowspan="${rowCount}">${tx?.discount ? tx?.discount : '-'}</td>` : ""}
                            
                            <!-- Columns 11-13: Payment Stack Summary -->
                            ${i === 0 ? `
                                <td rowspan="${rowCount}">${pAmt}</td>
                                <td rowspan="${rowCount}">${tTotal}</td>
                                <td rowspan="${rowCount}" style="font-weight:bold">${pAmt + tTotal}</td>
                            ` : ""}
                        </tr>
                    `;
                }).join("");
            }).join("")}
          </tbody>
        </table>
      </body>
      </html>
    `);

        window.document.close();
        window.focus();
    };

    return (
        <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
            {loading ? (
                <Box textAlign="center">
                    <CircularProgress />
                    <Typography sx={{ mt: 2 }}>Preparing your document...</Typography>
                </Box>
            ) : (
                <Typography>Print session finished. You can close this tab.</Typography>
            )}
        </Box>
    );
};

export default TransactionViewer;